import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import PhotoManager from "./PhotoManager";
import { BIO_MAX_LENGTH } from "./UserBio";
import { useI18n } from "../i18n";
import "./Profile.css";

const GENDER_OPTIONS = [
  { value: "MALE", label: "Male" },
  { value: "FEMALE", label: "Female" }
];

const GENDER_VALUES = GENDER_OPTIONS.map((option) => option.value);

/**
 * Legacy profiles may store free-form values such as "male" or "Female".
 * Map anything recognizable onto the canonical enum so the select can
 * preselect it; unknown values fall back to an empty selection.
 */
function normalizeGender(gender) {
  if (!gender) {
    return "";
  }

  const normalized = String(gender).trim().toUpperCase();

  return GENDER_VALUES.includes(normalized) ? normalized : "";
}

function Profile() {
  const MIN_AGE = 18;
  const MAX_AGE = 120;

  const { token, logout, updateUser } = useAuth();
  const { t } = useI18n();

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    age: "",
    gender: "",
    bio: "",
    photoUrl: "",
    videoUrl: "",
    country: "",
    city: ""
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    async function fetchProfile() {
      if (!token) {
        setError(t("auth.login"));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const response = await axios.get("/api/users/me", {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        const profile = response.data;

        setFormData({
          username: profile.username || "",
          email: profile.email || "",
          age: profile.age || "",
          gender: normalizeGender(profile.gender),
          bio: profile.bio || "",
          photoUrl: profile.photoUrl || "",
          videoUrl: profile.videoUrl || "",
          country: profile.country || "",
          city: profile.city || ""
        });
      } catch (err) {
        console.error("Profile load error:", err);

        if (err.response && (err.response.status === 401 || err.response.status === 403)) {
          logout();
          setError(t("auth.login"));
        } else if (err.response && err.response.data) {
          setError(`Failed to load profile: ${err.response.data}`);
        } else {
          setError("Failed to load profile.");
        }
      } finally {
        setLoading(false);
      }
    }

    fetchProfile();
  }, [token, logout]);

  function handleChange(event) {
    const { name, value } = event.target;

    setFormData((previousData) => ({
      ...previousData,
      [name]: value
    }));
  }

  function isValidAge(ageValue) {
    const numericAge = Number(ageValue);

    return (
      Number.isInteger(numericAge) &&
      numericAge >= MIN_AGE &&
      numericAge <= MAX_AGE
    );
  }

  function renderAgeOptions() {
    return Array.from(
      { length: MAX_AGE - MIN_AGE + 1 },
      (_, index) => {
        const age = MIN_AGE + index;

        return (
          <option key={age} value={age}>
            {age}
          </option>
        );
      }
    );
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (!token) {
      setError(t("auth.login"));
      return;
    }

    const numericAge = Number(formData.age);

    if (!isValidAge(formData.age)) {
      setError("Age must be between 18 and 120.");
      setSuccessMessage("");
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccessMessage("");

      const payload = {
        username: formData.username,
        age: numericAge,
        bio: formData.bio,
        photoUrl: formData.photoUrl,
        videoUrl: formData.videoUrl,
        country: formData.country,
        city: formData.city
      };

      const response = await axios.put(
        "/api/users/me",
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      const updatedProfile = response.data;

      setFormData({
        username: updatedProfile.username || "",
        email: updatedProfile.email || "",
        age: updatedProfile.age || "",
        gender: normalizeGender(updatedProfile.gender),
        bio: updatedProfile.bio || "",
        photoUrl: updatedProfile.photoUrl || "",
        videoUrl: updatedProfile.videoUrl || "",
        country: updatedProfile.country || "",
        city: updatedProfile.city || ""
      });

      if (updateUser) {
        updateUser(updatedProfile);
      }

      setSuccessMessage(t("profile.updated"));
    } catch (err) {
      console.error("Profile update error:", err);

      if (err.response && err.response.status === 409) {
        setError("Username is already taken.");
      } else if (err.response && err.response.status === 400 && err.response.data) {
        setError(err.response.data);
      } else if (err.response && err.response.status === 401) {
        logout();
        setError(t("auth.login"));
      } else if (err.response && err.response.status === 403) {
        setError("Access denied. Please check backend security settings.");
      } else if (err.response && err.response.data) {
        setError(`Failed to update profile: ${err.response.data}`);
      } else {
        setError("Failed to update profile.");
      }
    } finally {
      setSaving(false);
    }
  }


  if (loading) {
    return (
      <div className="profile-page">
        <div className="profile-shell">
          <div className="profile-header">
            <h1 className="profile-title">{t("profile.title")}</h1>
          </div>

          <div className="profile-card">
            <p className="profile-loading">{t("profile.loading")}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="profile-shell">
        <div className="profile-header">
          <h1 className="profile-title">{t("profile.title")}</h1>
          <p className="profile-subtitle">
            {t("profile.subtitle")}
          </p>
        </div>

        {error && <div className="profile-alert error">{error}</div>}

        {successMessage && (
          <div className="profile-alert success">{successMessage}</div>
        )}

        {formData.photoUrl && (
          <div className="profile-card">
            <h2 className="profile-card-title">{t("profile.photoPreview")}</h2>

            <div className="profile-media">
              <img
                src={formData.photoUrl}
                alt={t("profile.title")}
                className="profile-media-image"
                onError={(event) => {
                  event.currentTarget.style.display = "none";
                }}
              />
            </div>
          </div>
        )}

        {formData.videoUrl && (
          <div className="profile-card">
            <h2 className="profile-card-title">{t("profile.videoPreview")}</h2>

            <div className="profile-media">
              <video
                src={formData.videoUrl}
                controls
                className="profile-media-video"
              />
            </div>
          </div>
        )}

        <div className="profile-card">
          <PhotoManager />
        </div>

        <div className="profile-card">
          <h2 className="profile-card-title">{t("profile.accountDetails")}</h2>

          <form className="profile-form" onSubmit={handleSubmit}>
            <div className="profile-field">
              <label className="profile-label" htmlFor="profile-username">
                {t("profile.username")}
              </label>
              <input
                id="profile-username"
                type="text"
                name="username"
                className="profile-input"
                value={formData.username}
                onChange={handleChange}
              />
            </div>

            <div className="profile-field">
              <label className="profile-label" htmlFor="profile-email">
                {t("profile.email")}
              </label>
              <input
                id="profile-email"
                type="email"
                name="email"
                className="profile-input"
                value={formData.email}
                disabled
              />
              <span className="profile-hint">{t("profile.emailLocked")}</span>
            </div>

            <div className="profile-field">
              <label className="profile-label" htmlFor="profile-age">
                {t("profile.age")}
              </label>
              <select
                id="profile-age"
                name="age"
                className="profile-select"
                value={formData.age}
                onChange={handleChange}
                required
              >
                <option value="">{t("profile.selectAge")}</option>
                {renderAgeOptions()}
              </select>
            </div>

            <div className="profile-field">
              <label className="profile-label" htmlFor="profile-gender">
                {t("profile.gender")}
              </label>
              <select
                id="profile-gender"
                name="gender"
                className="profile-select"
                value={formData.gender}
                disabled
              >
                <option value="">{t("profile.notSet")}</option>
                {GENDER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <span className="profile-hint">
                {t("profile.genderLocked")}
              </span>
            </div>

            <div className="profile-field">
              <label className="profile-label" htmlFor="profile-country">
                {t("profile.country")}
              </label>
              <input
                id="profile-country"
                type="text"
                name="country"
                className="profile-input"
                value={formData.country}
                onChange={handleChange}
                placeholder={t("profile.examplePoland")}
              />
            </div>

            <div className="profile-field">
              <label className="profile-label" htmlFor="profile-city">
                {t("profile.city")}
              </label>
              <input
                id="profile-city"
                type="text"
                name="city"
                className="profile-input"
                value={formData.city}
                onChange={handleChange}
                placeholder={t("profile.exampleWarsaw")}
              />
            </div>

            <div className="profile-field">
              <label className="profile-label" htmlFor="profile-photo-url">
                {t("profile.photoUrl")}
              </label>
              <input
                id="profile-photo-url"
                type="text"
                name="photoUrl"
                className="profile-input"
                value={formData.photoUrl}
                onChange={handleChange}
                placeholder={t("profile.profileExamplePhoto")}
              />
            </div>

            <div className="profile-field">
              <label className="profile-label" htmlFor="profile-video-url">
                {t("profile.videoUrl")}
              </label>
              <input
                id="profile-video-url"
                type="text"
                name="videoUrl"
                className="profile-input"
                value={formData.videoUrl}
                onChange={handleChange}
                placeholder={t("profile.profileExampleVideo")}
              />
            </div>

            <div className="profile-field">
              <label className="profile-label" htmlFor="profile-bio">
                {t("profile.bio")}
              </label>
              <textarea
                id="profile-bio"
                name="bio"
                className="profile-textarea"
                value={formData.bio}
                onChange={handleChange}
                rows="4"
                maxLength={BIO_MAX_LENGTH}
              />
              <span className="profile-counter">
                {formData.bio.length} / {BIO_MAX_LENGTH}
              </span>
            </div>

            <button type="submit" className="profile-submit" disabled={saving}>
              {saving ? t("profile.saving") : t("profile.save")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Profile;
